import { createContext, useContext, useMemo, type ReactNode } from 'react';

/**
 * Supplies the data-API base URL to the data hooks. The @thema/ui package reads
 * no env itself (consumer-injects-config convention) — the app wraps the tree in
 * <DataApiProvider baseUrl={getEnv('VITE_DATA_API_URL')}> and the hooks read it
 * from here. Mirrors the OmtSelectorProvider pattern.
 */
interface DataApiConfig {
  baseUrl: string;
  /**
   * Optional bearer credential for the authenticated endpoints (the per-wallet
   * analytics API). The console injects the signed-in user's Google ID token
   * here; public dashboards leave it unset. Read by `useDataQuery({ authenticated
   * })` at fetch time so a token refresh flows through without re-keying queries.
   */
  authToken?: string | null;
}

const DataApiContext = createContext<DataApiConfig | null>(null);

export function DataApiProvider({
  baseUrl,
  authToken,
  children,
}: {
  baseUrl: string;
  authToken?: string | null;
  children: ReactNode;
}) {
  const value = useMemo<DataApiConfig>(() => ({ baseUrl, authToken }), [baseUrl, authToken]);
  return <DataApiContext.Provider value={value}>{children}</DataApiContext.Provider>;
}

function useDataApiConfig(): DataApiConfig {
  const ctx = useContext(DataApiContext);
  if (!ctx) {
    throw new Error(
      'Data-API hooks require a <DataApiProvider baseUrl={…}> ancestor (set VITE_DATA_API_URL).',
    );
  }
  return ctx;
}

/** Base URL for the data API. Throws if no <DataApiProvider> ancestor (fail loud). */
export function useDataApiBaseUrl(): string {
  return useDataApiConfig().baseUrl;
}

/** Current bearer token for authenticated endpoints (null when signed out). */
export function useDataApiAuthToken(): string | null {
  return useDataApiConfig().authToken ?? null;
}
